import { useEffect, useMemo, useState } from 'react';
import { MenuProps } from 'antd';
import { WorkspaceAccessResponse } from '../../api/workspace';
import { useParams } from 'react-router-dom';
import { useCreateEpic, useEpics, useUpdateEpicsOrderMutation } from '../../serverState/epics';
import { DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Epic } from '../../types/Epic';
import { useGetArchivedTasks, useGetTasks } from '../../serverState/tasks';
import { useGetActualSpaceId, useGetUsersWithWorkspaceAccess } from '../../serverState/workspace';
import { TaskTableDataType } from '../TaskTable/types/TaskTableTypes';
import { WorkspaceView, WorkspaceViewEntityType, WorkspaceViewGroup } from '../../types/WorkspaceView';
import { getCurrentWorkspaceActiveSprint } from '../../serverState/sprint';
import { buildVisibleSubitemIndex } from '../../utils/subitems';
import { scopeTasksForView } from '../../utils/taskListUtils';

// TODO: Create zustand store for the dataSource so it can be more easily managed
// or try updating the state of react query client for 'tasks' queryKey

const useManageTasks = (view?: WorkspaceView) => {
  const { spaceId } = useParams();

  const actualSpaceId = useGetActualSpaceId();
  const isArchiveView = view?.group === WorkspaceViewGroup.archive;
  const tasksQuery = useGetTasks(Number(actualSpaceId));
  const archivedTasksQuery = useGetArchivedTasks(Number(actualSpaceId), isArchiveView);
  const usersWithAccessToSpaceQuery = useGetUsersWithWorkspaceAccess(Number(actualSpaceId));
  const activeSprintId = getCurrentWorkspaceActiveSprint().data?.sprint.id;
  const taskSource = isArchiveView ? archivedTasksQuery.data : tasksQuery.data;

  const [dataSource, setDataSource] = useState<TaskTableDataType[]>([]);

  const [usersWithAccessToSpace, setUsersWithAccessToSpace] = useState<WorkspaceAccessResponse[]>([]);

  const epicsQuery = useEpics(Number(actualSpaceId));
  const [epics, setEpics] = useState<Epic[]>([]);

  useEffect(() => {
    if (epicsQuery.data) {
      setEpics(epicsQuery.data);
      if (view?.entityType === WorkspaceViewEntityType.epic) {
        // Goes through the shared predicate so the Epics table can't disagree with the
        // epic's own task view about which children it has (#2884) — indexed once for the
        // whole table instead of re-scanning the space list per epic (#2803).
        const visibleSubitemsOf = buildVisibleSubitemIndex(tasksQuery.data ?? []);
        setDataSource(
          epicsQuery.data.map((epic) => ({
            key: epic.id,
            ...epic,
            subtasks: visibleSubitemsOf(epic),
          }))
        );
      }
    }

    if (taskSource && view?.entityType === WorkspaceViewEntityType.task) {
      const isTriageView = view?.group === WorkspaceViewGroup.triage;
      const visibleSubitemsOf = buildVisibleSubitemIndex(taskSource ?? []);
      setDataSource(
        // Shared with the Task view's prev/next arrows so both walk the same list. #3242
        scopeTasksForView(taskSource, view?.group, activeSprintId as number | undefined).map((task) => {
          return {
            ...task,
            subtasks: isTriageView ? [] : visibleSubitemsOf(task),
          };
        })
      );
    }

    if (usersWithAccessToSpaceQuery.data) {
      setUsersWithAccessToSpace(usersWithAccessToSpaceQuery.data.sort((a, b) => a.userId - b.userId));
    }
  }, [
    epicsQuery.data,
    tasksQuery.data,
    archivedTasksQuery.data,
    usersWithAccessToSpaceQuery.data,
    view,
    activeSprintId,
  ]);

  const { createEpic, createEpicMutation } = useCreateEpic();
  const newItemMenu: MenuProps['items'] = [
    {
      label: 'New Epic',
      key: '1',
      onClick: async () => {
        await createEpic(Number(actualSpaceId), 'New epic');
      },
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    console.log('click', e);
  };

  const taskMenuProps = {
    items: newItemMenu,
    onClick: handleMenuClick,
  };

  const updateEpicsOrderMutation = useUpdateEpicsOrderMutation();

  const onDragEnd = (event: any) => {
    setActiveEpic(null);
    setActiveTask(null);

    const { active, over } = event;

    if (!over) return;

    if (active?.id === over?.id) {
      return;
    }

    const isActiveAEpic = active.data.current?.type === 'Epic';
    if (!isActiveAEpic) return;

    const activeEpicIndex = epics.findIndex((epic) => epic.id === active.id);
    const overEpicIndex = epics.findIndex((epic) => epic.id === over.id);

    const newEpics = arrayMove(epics, activeEpicIndex, overEpicIndex);

    setEpics(newEpics);

    // Update order on backend
    // await updateEpicsOrder(spaceId, epics.map(epic => epic.id));
    updateEpicsOrderMutation.mutate(
      {
        workspaceId: spaceId,
        epicIds: newEpics.map((epic) => epic.id),
      },
      {
        onSuccess: () => {},
      }
    );

    // console.log(event)
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const [activeEpic, setActiveEpic] = useState<Epic | null>(null);

  const [activeTask, setActiveTask] = useState<TaskTableDataType | null>(null);

  const onDragStart = (event: DragStartEvent) => {
    // console.log(event)
    if (event.active.data.current?.type === 'Epic') {
      setActiveEpic(event.active.data.current?.epic);
      return;
    }
    if (event.active.data.current?.type === 'Task') {
      setActiveTask(event.active.data.current?.task);
      return;
    }
  };

  const epicIds = useMemo(() => epics.map((p) => p.id), [epics]);

  // Return the relevant state and functions that other components may use
  return {
    actualSpaceId: Number(actualSpaceId),
    spaceId,
    tableDataLoading:
      (isArchiveView ? archivedTasksQuery.isLoading : tasksQuery.isLoading) || usersWithAccessToSpaceQuery.isLoading,
    usersWithAccessToSpace,
    dataSource,
    setDataSource,
    epics,
    epicIds,
    onDragStart,
    onDragEnd,
    activeEpic,
    sensors,
    taskMenuProps,
  };
};

export default useManageTasks;
